import { useEffect, useMemo, useState, useCallback } from 'react';
import { staffApi, formatApiError } from '@/lib/api';
import { useStaffAuth } from '@/contexts/StaffAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { fmtEdTime, fmtEdDate, edDateInput, edTimeInput, edParts, edAddDays, isoFromEdmonton } from '@/lib/tz';
import { toast } from 'sonner';
import { Clock, Pencil, Trash2, PlusCircle, DollarSign, Download, Lock, LockOpen, ShieldCheck, Coffee } from 'lucide-react';

const fmtDur = (m) => `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
const money = (n) => `$${n.toFixed(2)}`;

// Sunday-start week containing today, as {from,to} YYYY-MM-DD Edmonton date strings.
function currentWeek() {
  const now = new Date();
  const wd = edParts(now).weekday;
  return { from: edDateInput(edAddDays(now, -wd)), to: edDateInput(edAddDays(now, 6 - wd)) };
}

export default function Timesheet() {
  const { staff: me } = useStaffAuth();
  const isAdmin = me?.role === 'ADMIN';
  const [range, setRange] = useState(currentWeek);
  const [entries, setEntries] = useState([]);
  const [staff, setStaff] = useState([]);
  const [approval, setApproval] = useState(null); // { approved, approval:{weekKey, approvedByName, approvedAt} }
  const [breaksPaid, setBreaksPaid] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);

  const locked = !!approval?.approved;
  const canEdit = isAdmin || !locked;

  const load = useCallback(() => {
    setLoading(true);
    staffApi.get('/timeclock/entries', { params: { from: range.from, to: range.to } })
      .then(({ data }) => { setEntries(data.entries); setBreaksPaid(!!data.breaksPaid); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
    staffApi.get('/timeclock/approval', { params: { weekStart: range.from } })
      .then(({ data }) => setApproval(data))
      .catch(() => setApproval(null));
  }, [range.from, range.to]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { staffApi.get('/scheduling/staff').then(({ data }) => setStaff(data.staff)).catch(() => {}); }, []);

  const groups = useMemo(() => {
    const map = {};
    for (const e of entries) {
      if (!map[e.staffId]) map[e.staffId] = { staffId: e.staffId, staffName: e.staffName, hourlyWage: e.hourlyWage, entries: [], minutes: 0, breaks: 0, open: 0 };
      const g = map[e.staffId];
      g.entries.push(e);
      g.breaks += e.breakMinutes || 0;
      if (e.minutes != null) g.minutes += e.minutes;
      else g.open += 1;
    }
    return Object.values(map).sort((a, b) => a.staffName.localeCompare(b.staffName));
  }, [entries]);

  const totals = useMemo(() => {
    let minutes = 0; let cost = 0;
    for (const g of groups) { minutes += g.minutes; if (g.hourlyWage) cost += (g.minutes / 60) * g.hourlyWage; }
    return { minutes, cost };
  }, [groups]);

  const approve = async () => {
    try {
      await staffApi.post('/timeclock/approval', { weekStart: range.from });
      toast.success('Week approved & locked');
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const unlock = async () => {
    if (!window.confirm('Unlock this week for edits? Payroll totals can change until it is re-approved.')) return;
    try {
      await staffApi.delete(`/timeclock/approval/${encodeURIComponent(approval.approval.weekKey)}`);
      toast.success('Week unlocked');
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this time entry?')) return;
    try { await staffApi.delete(`/timeclock/entries/${id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleBreaksPaid = async (val) => {
    try {
      await staffApi.patch('/scheduling/settings', { breaksPaid: val });
      setBreaksPaid(val);
      toast.success(val ? 'Breaks are now PAID' : 'Breaks are now UNPAID');
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const exportCsv = () => {
    const rows = [['Staff', 'Date', 'Clock In', 'Clock Out', 'Hours', 'Source', 'Note']];
    for (const g of groups) for (const e of g.entries) {
      rows.push([
        g.staffName,
        fmtEdDate(e.clockIn, { year: 'numeric', month: 'short', day: 'numeric' }),
        fmtEdTime(e.clockIn),
        e.clockOut ? fmtEdTime(e.clockOut) : 'Active',
        e.minutes != null ? (e.minutes / 60).toFixed(2) : '',
        e.source,
        (e.note || '').replace(/"/g, '""'),
      ]);
    }
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = `timesheet_${range.from}_${range.to}.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-2xl font-700 text-charcoal flex items-center gap-2"><Clock size={22} className="text-saffron" /> Timesheet</h1>
        <div className="flex items-center gap-2">
          <Button data-testid="timesheet-export-btn" variant="outline" size="sm" onClick={exportCsv} className="gap-1.5"><Download size={14} /> Export CSV</Button>
          <Button data-testid="timesheet-add-btn" size="sm" disabled={!canEdit} onClick={() => setAdding(true)} className="bg-saffron hover:bg-saffron-hover gap-1.5"><PlusCircle size={14} /> Add Entry</Button>
        </div>
      </div>

      <div className={`rounded-xl border p-4 flex flex-wrap items-center justify-between gap-3 ${locked ? 'bg-coriander/10 border-coriander/30' : 'bg-white border-border/40'}`} data-testid="timesheet-approval-banner">
        <div className="flex items-center gap-2.5">
          {locked ? <Lock size={18} className="text-coriander shrink-0" /> : <ShieldCheck size={18} className="text-muted-ink shrink-0" />}
          <div className="text-sm">
            {locked ? (
              <>
                <p className="font-medium text-charcoal" data-testid="timesheet-locked-label">Week locked & approved for payroll</p>
                <p className="text-xs text-muted-ink">By {approval.approval?.approvedByName || 'staff'} · {fmtEdDate(approval.approval?.approvedAt, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}{!isAdmin ? ' · ask an admin to unlock for edits' : ''}</p>
              </>
            ) : (
              <>
                <p className="font-medium text-charcoal">This week is not approved yet</p>
                <p className="text-xs text-muted-ink">Review the hours below, then lock the week so it counts toward payroll.</p>
              </>
            )}
          </div>
        </div>
        {locked ? (
          isAdmin && <Button data-testid="timesheet-unlock-btn" variant="outline" size="sm" onClick={unlock} className="gap-1.5 border-coriander text-coriander hover:bg-coriander/10"><LockOpen size={14} /> Unlock Week</Button>
        ) : (
          <Button data-testid="timesheet-approve-btn" size="sm" onClick={approve} className="bg-coriander hover:bg-coriander/90 gap-1.5"><Lock size={14} /> Approve &amp; Lock Week</Button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-border/40 p-4 flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-xs">From</Label>
          <Input data-testid="timesheet-from" type="date" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} className="w-40" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">To</Label>
          <Input data-testid="timesheet-to" type="date" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} className="w-40" />
        </div>
        <Button data-testid="timesheet-thisweek" variant="ghost" size="sm" onClick={() => setRange(currentWeek())} className="text-saffron">This week</Button>
        <div className="ml-auto flex items-center gap-2.5 rounded-lg bg-bone px-3 py-2" data-testid="timesheet-breakspaid-row">
          <Coffee size={15} className={breaksPaid ? 'text-coriander' : 'text-muted-ink'} />
          <div className="text-xs leading-tight">
            <p className="font-medium text-charcoal">Paid breaks</p>
            <p className="text-muted-ink">{breaksPaid ? 'On — breaks count as paid time' : 'Off — breaks are unpaid'}</p>
          </div>
          <Switch data-testid="timesheet-breakspaid-switch" checked={breaksPaid} disabled={!isAdmin} onCheckedChange={toggleBreaksPaid} />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-border/40 p-4">
          <p className="text-xs text-muted-ink">Total Hours</p>
          <p className="font-heading text-xl font-700 text-charcoal" data-testid="timesheet-total-hours">{fmtDur(totals.minutes)}</p>
        </div>
        <div className="bg-white rounded-xl border border-border/40 p-4">
          <p className="text-xs text-muted-ink flex items-center gap-1"><DollarSign size={12} /> Est. Labor Cost</p>
          <p className="font-heading text-xl font-700 text-charcoal" data-testid="timesheet-total-cost">{money(totals.cost)}</p>
        </div>
        <div className="bg-white rounded-xl border border-border/40 p-4">
          <p className="text-xs text-muted-ink">Employees</p>
          <p className="font-heading text-xl font-700 text-charcoal">{groups.length}</p>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-ink">Loading…</p>
      ) : groups.length === 0 ? (
        <div className="bg-white rounded-xl border border-border/40 p-6 text-center text-sm text-muted-ink" data-testid="timesheet-empty">No time entries in this range.</div>
      ) : (
        <div className="space-y-3">
          {groups.map((g) => (
            <div key={g.staffId} className="bg-white rounded-xl border border-border/40 overflow-hidden" data-testid="timesheet-staff-group">
              <div className="flex items-center justify-between px-4 py-2.5 bg-bone/60">
                <p className="font-heading font-700 text-charcoal">{g.staffName}</p>
                <p className="text-sm text-muted-ink">
                  <span className="font-semibold text-charcoal">{fmtDur(g.minutes)}</span>
                  {g.hourlyWage ? ` · ${money((g.minutes / 60) * g.hourlyWage)}` : ''}
                  {g.breaks ? ` · ${g.breaks}m break` : ''}
                  {g.open ? ` · ${g.open} active` : ''}
                </p>
              </div>
              <div className="divide-y divide-border/30">
                {g.entries.map((e) => (
                  <div key={e.id} className="flex items-center gap-3 px-4 py-2.5 text-sm" data-testid="timesheet-entry-row">
                    <span className="text-muted-ink w-28 shrink-0">{fmtEdDate(e.clockIn, { month: 'short', day: 'numeric' })}</span>
                    <span className="text-charcoal">{fmtEdTime(e.clockIn)} → {e.clockOut ? fmtEdTime(e.clockOut) : <span className="text-coriander font-medium">Active</span>}</span>
                    <span className="text-muted-ink">{e.minutes != null ? fmtDur(e.minutes) : ''}</span>
                    {e.breakMinutes > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 flex items-center gap-0.5"><Coffee size={9} /> {e.breakMinutes}m</span>}
                    {e.source === 'MANUAL' && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-bone text-muted-ink">manual</span>}
                    {canEdit && (
                      <div className="ml-auto flex items-center gap-2">
                        <button data-testid="timesheet-edit-btn" onClick={() => setEditing(e)} className="text-muted-ink hover:text-charcoal"><Pencil size={15} /></button>
                        <button data-testid="timesheet-delete-btn" onClick={() => remove(e.id)} className="text-destructive"><Trash2 size={15} /></button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <EntryDialog entry={editing} onClose={() => setEditing(null)} onDone={load} />}
      {adding && <EntryDialog staff={staff} onClose={() => setAdding(false)} onDone={load} />}
    </div>
  );
}

function EntryDialog({ entry, staff, onClose, onDone }) {
  const isEdit = !!entry;
  const [staffId, setStaffId] = useState(entry?.staffId || '');
  const [date, setDate] = useState(edDateInput(entry?.clockIn || new Date()));
  const [inTime, setInTime] = useState(edTimeInput(entry?.clockIn || new Date()));
  const [outDate, setOutDate] = useState(edDateInput(entry?.clockOut || entry?.clockIn || new Date()));
  const [outTime, setOutTime] = useState(entry?.clockOut ? edTimeInput(entry.clockOut) : '');
  const [note, setNote] = useState(entry?.note || '');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!isEdit && !staffId) return toast.error('Choose a staff member');
    const clockIn = isoFromEdmonton(date, inTime).toISOString();
    const clockOut = outTime ? isoFromEdmonton(outDate, outTime).toISOString() : null;
    setBusy(true);
    try {
      if (isEdit) await staffApi.patch(`/timeclock/entries/${entry.id}`, { clockIn, clockOut: clockOut ?? '', note });
      else await staffApi.post('/timeclock/entries', { staffId, clockIn, clockOut, note });
      toast.success(isEdit ? 'Entry updated' : 'Entry added');
      onClose();
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="timesheet-entry-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Time Entry' : 'Add Time Entry'}</DialogTitle>
          <DialogDescription>Times are in Edmonton (Mountain) time. Leave clock-out empty to keep it active.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {!isEdit && (
            <div className="space-y-1.5">
              <Label>Staff member</Label>
              <Select value={staffId} onValueChange={setStaffId}>
                <SelectTrigger data-testid="entry-staff-select"><SelectValue placeholder="Select staff" /></SelectTrigger>
                <SelectContent>
                  {(staff || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Date (in)</Label><Input data-testid="entry-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Clock in</Label><Input data-testid="entry-in-time" type="time" value={inTime} onChange={(e) => setInTime(e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Date (out)</Label><Input data-testid="entry-out-date" type="date" value={outDate} onChange={(e) => setOutDate(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Clock out</Label><Input data-testid="entry-out-time" type="time" value={outTime} onChange={(e) => setOutTime(e.target.value)} /></div>
          </div>
          <div className="space-y-1.5"><Label>Note (optional)</Label><Input data-testid="entry-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. forgot to punch out" /></div>
        </div>
        <DialogFooter>
          <Button data-testid="entry-submit-btn" disabled={busy} onClick={submit} className="bg-saffron hover:bg-saffron-hover">{busy ? 'Saving…' : isEdit ? 'Save' : 'Add Entry'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
